import React, { useState } from 'react';

export function EventForm({ initialEvent, onSubmit, onCancel }) {
  const [formData, setFormData] = useState(
    initialEvent || {
      name: '',
      description: '',
      date: '',
      time: '',
      location: '',
      capacity: 100,
      price: 0,
      image_url: '',
      is_active: 1,
      is_public: 1,
      displayed_order: 0,
      allow_registrations: 1,
    }
  );

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : type === 'number' ? parseFloat(value) : value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="card">
      <div className="card-header">
        <h5>{initialEvent?.id ? 'Edit Event' : 'Create New Event'}</h5>
      </div>
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="mb-3">
            <label htmlFor="name" className="form-label">Event Name</label>
            <input
              type="text"
              className="form-control"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="description" className="form-label">Description</label>
            <textarea
              className="form-control"
              id="description"
              name="description"
              rows="3"
              value={formData.description}
              onChange={handleChange}
              required
            ></textarea>
          </div>
          <div className="row">
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="date" className="form-label">Date</label>
                <input
                  type="date"
                  className="form-control"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <label htmlFor="time" className="form-label">Time</label>
                <input
                  type="time"
                  className="form-control"
                  id="time"
                  name="time"
                  value={formData.time}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>
          <div className="mb-3">
            <label htmlFor="location" className="form-label">Location</label>
            <input
              type="text"
              className="form-control"
              id="location"
              name="location"
              value={formData.location}
              onChange={handleChange}
              required
            />
          </div>
          <div className="mb-3">
            <label htmlFor="capacity" className="form-label">Capacity</label>
            <input
              type="number"
              className="form-control"
              id="capacity"
              name="capacity"
              value={formData.capacity}
              onChange={handleChange}
              min="1"
              required
            />
          </div>

          <div className="mb-3">
            <label className="form-label text-dark">Ticket Price ($) (0 for Free)</label>
            <input
              type="number"
              className="form-control"
              name="price"
              value={formData.price}
              onChange={handleChange}
              min="0"
              step="0.01"
              required
            />
          </div>

          <div className="mb-3">
            <label htmlFor="image_upload" className="form-label">Event Thumbnail Image</label>
            <div className="d-flex gap-2 mb-2">
              <input
                type="file"
                className="form-control"
                id="image_upload"
                accept="image/*"
                onChange={async (e) => {
                  const file = e.target.files[0];
                  if (!file) return;
                  
                  const uploadData = new FormData();
                  uploadData.append('file', file);
                  uploadData.append('type', 'image');
                  
                  try {
                    const res = await fetch('/api/upload', {
                      method: 'POST',
                      body: uploadData
                    });
                    if (!res.ok) throw new Error('Upload failed');
                    const data = await res.json();
                    const finalUrl = data.path.startsWith('/') ? data.path : '/' + data.path.replace(/\\/g, '/');
                    setFormData(prev => ({ ...prev, image_url: finalUrl }));
                  } catch (err) {
                    console.error('Image upload failed:', err);
                    alert('Failed to upload image. Please try again.');
                  }
                }}
              />
            </div>
            {formData.image_url && (
              <div className="mt-2">
                <img src={formData.image_url} alt="Preview" style={{ maxHeight: '100px', borderRadius: '4px' }} />
              </div>
            )}
            <small className="form-text text-muted">Upload an image or enter a URL below</small>
            <input
              type="text"
              className="form-control mt-2"
              id="image_url"
              name="image_url"
              value={formData.image_url || ''}
              onChange={handleChange}
              placeholder="https://example.com/image.jpg"
            />
          </div>

          <div className="mb-3">
            <label htmlFor="displayed_order" className="form-label">Display Order</label>
            <input
              type="number"
              className="form-control"
              id="displayed_order"
              name="displayed_order"
              value={formData.displayed_order || 0}
              onChange={handleChange}
              min="0"
            />
            <small className="form-text text-muted">Lower numbers appear first</small>
          </div>

          <div className="row">
            <div className="col-md-6">
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="is_active"
                  name="is_active"
                  checked={formData.is_active === 1}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="is_active">
                  ✓ Event is Active
                </label>
                <small className="form-text text-muted d-block">Deactivate to hide event temporarily</small>
              </div>
            </div>
            <div className="col-md-6">
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="is_public"
                  name="is_public"
                  checked={formData.is_public === 1}
                  onChange={handleChange}
                />
                <label className="form-check-label" htmlFor="is_public">
                  🌐 Event is Public
                </label>
                <small className="form-text text-muted d-block">Uncheck to make event private</small>
              </div>
            </div>
          </div>

          <div className="mb-3 form-check">
            <input
              type="checkbox"
              className="form-check-input"
              id="allow_registrations"
              name="allow_registrations"
              checked={formData.allow_registrations === 1}
              onChange={handleChange}
            />
            <label className="form-check-label" htmlFor="allow_registrations">
              ✍️ Allow Registrations
            </label>
            <small className="form-text text-muted d-block">Uncheck to close event registrations</small>
          </div>

          <div className="d-flex gap-2">
            <button type="submit" className="btn btn-success">
              Save Event
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={onCancel}
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
